<template>
  <div class="container-fluid container-boxed">
    <div class="card">
      <div class="card-body">
        <h2>Program and Placement Details</h2>

        <no-results v-if="haveNoPlacements">
          Your agencies are not currently assigned to any placements.
        </no-results>
        <div v-for="program in groupedPlacements" :key="program.id">
          <h3>{{ program.name }}</h3>
          <active-placement-item v-for="placement in program.placements" :placement="placement" :key="placement.id"/>
        </div>
      </div>
    </div><!-- /.card -->

    <div class="decorative-divider"></div>

    <div class="card">
      <div class="card-header">
        <div class="row">
          <div class="col">
            <h2 class="card-header-title mt-3">Assigned Graduates</h2>
          </div>
        </div><!-- /.row -->
      </div>

      <ul class="list-data">
        <loading-spinner v-if="fetching"/>
        <no-results v-else-if="haveNoAssignedGraduates">
          No graduates currently assigned to you.
        </no-results>
        <graduate-placement-item v-for="placement in assignedGraduates" :placement="placement" :key="placement.graduate_placement_id"/>
      </ul><!-- /.list-data -->
    </div><!-- /.card -->

    <div class="decorative-divider"></div>

    <div class="card">
      <div class="card-header">
        <h2 class="card-header-title">Tasks</h2>
      </div>

      <ul class="list-data">
        <no-results v-if="haveNoTasks">
          There are no tasks available.
        </no-results>
        <task-list-item v-for="task in tasks" :task="task" :key="task.id"/>
      </ul><!-- /.list-data -->
    </div><!-- /.card -->

    <div class="decorative-divider"></div>

    <div class="card">
      <div class="card-header">
        <div class="row">
          <div class="col">
            <h2 class="card-header-title mt-3">Current Graduates</h2>
          </div>
        </div><!-- /.row -->
      </div>

      <ul class="list-data">
        <loading-spinner v-if="fetching"/>
        <no-results v-else-if="haveNoGraduatePlacements">
          No graduates currently assigned to your agencies.
        </no-results>
        <graduate-placement-item v-for="placement in currentGraduates" :placement="placement" :key="placement.graduate_placement_id"/>
      </ul><!-- /.list-data -->
    </div><!-- /.card -->

    <div class="card">
      <div class="card-header">
        <div class="row">
          <div class="col">
            <h2 class="card-header-title mt-3">Overall Performance Assessment</h2>
          </div>
        </div><!-- /.row -->
      </div>

      <ul class="list-data">
        <loading-spinner v-if="fetching"/>
        <no-results v-else-if="haveNoPerformanceAssessments">
          No assessments currently assigned to your agencies.
        </no-results>
        <li v-for="assessment in this.performance_assessments" :key="assessment.id">
          <router-link :to="{ name: 'graduates.assessments.single', params: { graduateId: assessment.graduate_id } }">
            <div class="row">
              <div class="col">
                <p class="strong">{{ assessment.graduate_name }}</p>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <ul class="details">
                  <li>
                    Program: {{ assessment.program }}
                  </li>
                </ul>
              </div>
              <div class="col">
                <ul class="details">
                  <li>
                    Overall Performance Assessment
                  </li>
                </ul>
              </div>
              <div class="col">
                <ul class="details">
                  <li>
                    Manager: {{ assessment.manager }}
                  </li>
                  <li>
                    Status: {{ assessment.status }}
                  </li>
                </ul>
              </div>
            </div>

          </router-link>
        </li>
      </ul><!-- /.list-data -->
    </div><!-- /.card -->

  </div><!-- /.container-fluid -->
</template>

<script>
import { taskComputed, taskMethods, dashboardComputed, dashboardMethods, authComputed } from 'vx/helpers'
import { map, groupBy, filter } from 'lodash'
import ActivePlacementItem from './DashboardActivePlacementItem'
import GraduatePlacementItem from './DashboardGraduatePlacementItem'
import TaskListItem from './DashboardTaskListItem'

export default {
  components: {
    TaskListItem,
    ActivePlacementItem,
    GraduatePlacementItem
  },

  data () {
    return {
      fetching: false
    }
  },

  computed: {
    ...dashboardComputed,
    ...taskComputed,
    ...authComputed,

    /**
     * Current graduates coordinator is assigned to
     */
    assignedGraduates () {
      return filter(this.graduate_placements, (placement) => {
        return placement.manager_id === this.user.id
      })
    },

    /**
     * Current graduates associated with agency
     */
    currentGraduates () {
      return filter(this.graduate_placements, (placement) => {
        return placement.manager_id !== this.user.id
      })
    },

    /**
     * The placements grouped by their program
     */
    groupedPlacements () {
      return map(groupBy(this.placements, 'program_id'), function (placements) {
        return {
          id: placements[0].program_id,
          name: placements[0].program.name,
          placements
        }
      })
    },

    haveNoAssignedGraduates () {
      return this.assignedGraduates.length === 0
    },

    haveNoGraduatePlacements () {
      return this.currentGraduates.length === 0
    },

    haveNoPlacements () {
      return this.groupedPlacements.length === 0
    },

    haveNoTasks () {
      return this.tasks.length === 0
    },

    haveNoPerformanceAssessments () {
      return this.performance_assessments.length === 0
    }
  },

  async created () {
    this.fetching = true
    await this.getActivePlacements()
    await this.getGraduatePlacements()
    await this.getTasks()
    await this.getPerformanceAssessments()
    this.fetching = false
  },

  methods: {
    ...taskMethods,
    ...dashboardMethods
  }
}
</script>
